import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Zap, Shield, AlertTriangle, Sparkles, ArrowRight, Layers, Compass, Crown, RefreshCw } from 'lucide-react';
import { cn } from '../lib/utils';
import { ESTRATEGIA_LABELS, ESTRATEGIA_BADGES } from './EstrategiaPortafolioSelector';

const TIPO_TONO = {
    'Tractor': { color: 'bg-emerald-100 text-emerald-900 border-emerald-200', icon: Zap, dot: 'bg-emerald-500' },
    'Mantención': { color: 'bg-slate-100 text-slate-800 border-slate-200', icon: Shield, dot: 'bg-slate-400' },
    'Alto Desgaste': { color: 'bg-rose-100 text-rose-900 border-rose-200', icon: AlertTriangle, dot: 'bg-rose-500' },
    'Focalizado': { color: 'bg-violet-100 text-violet-900 border-violet-200', icon: Crown, dot: 'bg-violet-500' },
};

const CLUSTER_TONO = {
    outlier: 'border-slate-300 bg-slate-50',
    planificacion_tecnica: 'border-sky-300 bg-sky-50/60',
    practica_aula: 'border-rose-300 bg-rose-50/60',
    reflexion_participacion: 'border-emerald-300 bg-emerald-50/60',
};

function Bars({ valor, max = 5, color = 'bg-slate-700' }) {
    return (
        <div className="flex gap-0.5">
            {Array.from({ length: max }, (_, i) => (
                <span key={i} className={cn('h-1.5 w-2 rounded-full', i < valor ? color : 'bg-slate-200')} />
            ))}
        </div>
    );
}

function IndicadorPill({ ind, dense = false }) {
    const tono = TIPO_TONO[ind.tipo] || TIPO_TONO['Focalizado'];
    const Icon = tono.icon;
    return (
        <div
            className={cn(
                'flex items-start gap-2 rounded-lg border bg-white p-3 hover:shadow-sm transition-all',
                dense && 'p-2.5'
            )}
            data-testid={`motor-indicador-${ind.id}`}
        >
            <div className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-md', tono.color)}>
                <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500">{ind.id}</span>
                    <span className={cn('text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded border', tono.color)}>
                        {ind.tipo}
                    </span>
                </div>
                <p className="mt-0.5 text-sm font-medium text-slate-900 leading-snug">{ind.nombre}</p>
                {ind.estrategia_corta && !dense && (
                    <p className="mt-1 text-[11px] text-slate-600 italic leading-relaxed">{ind.estrategia_corta}</p>
                )}
            </div>
        </div>
    );
}

function ClusterCard({ cluster }) {
    return (
        <div
            className={cn('rounded-2xl border-2 p-5 space-y-3', CLUSTER_TONO[cluster.id] || 'border-slate-200 bg-white')}
            data-testid={`motor-cluster-${cluster.id}`}
        >
            <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm">
                    <Layers className="h-4 w-4 text-slate-600" />
                </div>
                <div>
                    <h4 className="font-heading text-sm font-bold text-slate-900">{cluster.nombre}</h4>
                    <p className="mt-1 text-[11px] text-slate-700 leading-relaxed">{cluster.descripcion}</p>
                </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
                {cluster.indicadores_full.map((ind) => (
                    <IndicadorPill key={ind.id} ind={ind} dense />
                ))}
            </div>
        </div>
    );
}

function EfectoDominoCard({ pieza }) {
    return (
        <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50/80 to-white p-4" data-testid={`domino-${pieza.tractor}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1">
                    <Zap className="h-3 w-3" /> Tractor
                </span>
                <span className="font-semibold text-slate-900 text-sm">{pieza.tractor} {pieza.tractor_nombre}</span>
            </div>
            <div className="mt-3 flex items-start gap-2 text-xs text-slate-700">
                <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700" />
                <span>
                    Arrastra a: <strong className="text-slate-900">{pieza.arrastra_full.map((i) => i.nombre).join(' · ')}</strong>
                </span>
            </div>
            <p className="mt-2 text-[11px] italic text-slate-600 leading-relaxed">{pieza.explicacion}</p>
        </div>
    );
}

export default function MotorEstrategicoView() {
    const { profile } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const estrategiaActiva = profile?.perfil?.estrategia_portafolio || 'mixta';

    const fetchRecomendaciones = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get('/strategy/recommendations');
            const d = response.data;
            if (!d || typeof d !== 'object' || !d.recomendaciones) {
                throw new Error('Respuesta inesperada del Motor Estratégico');
            }
            setData(d);
        } catch (e) {
            console.error('Motor Estratégico — error:', e);
            setError(e?.response?.data?.detail || e?.message || 'Error de conexión');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Espera a que profile esté disponible para evitar race con auth/cookies de Google
        if (profile === null) return;
        let cancelled = false;
        (async () => {
            await fetchRecomendaciones();
            if (cancelled) return;
        })();
        return () => { cancelled = true; };
    }, [estrategiaActiva, profile, fetchRecomendaciones]);

    if (loading) {
        return (
            <div className="flex h-40 items-center justify-center" data-testid="motor-loading">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
        );
    }

    if (error || !data || !data.recomendaciones) {
        return (
            <Card className="border-amber-200 bg-amber-50/50" data-testid="motor-error-card">
                <CardContent className="p-5 space-y-3">
                    <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                            <AlertTriangle className="h-4 w-4" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-semibold text-amber-900">
                                No pudimos cargar las recomendaciones estratégicas en este momento.
                            </p>
                            <p className="mt-1 text-xs text-amber-800/80 leading-relaxed">
                                Puedes intentar nuevamente. Si el problema persiste, revisa tu conexión o vuelve a iniciar sesión.
                            </p>
                            {error && (
                                <p className="mt-2 text-[11px] font-mono text-amber-700/70 break-all">
                                    {String(error).slice(0, 200)}
                                </p>
                            )}
                        </div>
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        className="gap-2 border-amber-300 text-amber-900 hover:bg-amber-100"
                        onClick={fetchRecomendaciones}
                        data-testid="motor-retry-btn"
                    >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Reintentar
                    </Button>
                </CardContent>
            </Card>
        );
    }

    const rec = data.recomendaciones;
    const destacadoList = Array.isArray(rec.destacado_candidatos) ? rec.destacado_candidatos : [];
    const competenteList = Array.isArray(rec.asegurar_competente) ? rec.asegurar_competente : [];
    const advertenciasList = Array.isArray(rec.advertencias) ? rec.advertencias : [];
    const principiosList = Array.isArray(data.principios) ? data.principios : [];
    const dominoList = Array.isArray(data.efecto_domino) ? data.efecto_domino : [];
    const clustersList = Array.isArray(data.clusters) ? data.clusters : [];

    return (
        <div className="space-y-6" data-testid="motor-estrategico-view">
            {/* Hero */}
            <div className="rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50/80 via-white to-rose-50/40 p-6">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                        <Compass className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                        <Badge className="bg-amber-200 text-amber-900 text-[10px] font-bold uppercase tracking-wider border border-amber-300">Motor Estratégico</Badge>
                        <h2 className="mt-2 font-heading text-lg font-bold text-slate-900 leading-snug">
                            Tu mapa estratégico del Portafolio
                        </h2>
                        <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">
                            Basado en correlaciones, clustering jerárquico y perfiles nacionales de los 18 indicadores oficiales del Portafolio 2026 (7 + 7 + 4).
                            El sistema usa este motor para acompañarte con criterio profesional según tu estrategia activa:{' '}
                            <strong className="text-amber-900">{ESTRATEGIA_LABELS[rec.estrategia]}</strong>
                            {ESTRATEGIA_BADGES[rec.estrategia] && (
                                <>
                                    {' '}
                                    <span
                                        className="inline-flex items-center rounded-md bg-slate-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white align-middle"
                                        data-testid="motor-badge-acontista"
                                    >
                                        {ESTRATEGIA_BADGES[rec.estrategia]}
                                    </span>
                                </>
                            )}
                            .
                        </p>
                    </div>
                </div>
                <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                    {principiosList.map((p, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-600" />
                            <span className="leading-relaxed">{p}</span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Recomendaciones priorizadas */}
            <div className="grid gap-4 lg:grid-cols-3">
                {/* Destacado candidatos */}
                <div className="rounded-2xl border-2 border-violet-200 bg-white p-5 space-y-3" data-testid="motor-destacado-block">
                    <div className="flex items-center gap-2">
                        <Crown className="h-4 w-4 text-violet-700" />
                        <h3 className="font-heading text-sm font-bold text-slate-900">Candidatos a Destacado</h3>
                        <Badge variant="outline" className="text-[10px] ml-auto">{destacadoList.length}</Badge>
                    </div>
                    <p className="text-[11px] text-slate-500 italic leading-relaxed">Indicadores donde puedes apuntar alto con esfuerzo bien dirigido.</p>
                    <div className="space-y-2">
                        {destacadoList.length === 0 ? (
                            <p className="text-xs text-slate-500 italic">Tu estrategia prioriza coherencia. Ningún indicador exige apuntar a Destacado.</p>
                        ) : (
                            destacadoList.map((ind) => <IndicadorPill key={ind.id} ind={ind} />)
                        )}
                    </div>
                </div>

                {/* Asegurar Competente */}
                <div className="rounded-2xl border-2 border-slate-200 bg-white p-5 space-y-3" data-testid="motor-competente-block">
                    <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-slate-700" />
                        <h3 className="font-heading text-sm font-bold text-slate-900">Asegurar Competente</h3>
                        <Badge variant="outline" className="text-[10px] ml-auto">{competenteList.length}</Badge>
                    </div>
                    <p className="text-[11px] text-slate-500 italic leading-relaxed">Indicadores donde tu energía debe alcanzar lo suficiente sin sobreinvertir.</p>
                    <div className="space-y-2">
                        {competenteList.map((ind) => <IndicadorPill key={ind.id} ind={ind} />)}
                    </div>
                </div>

                {/* Advertencias */}
                <div className="rounded-2xl border-2 border-rose-200 bg-white p-5 space-y-3" data-testid="motor-advertencias-block">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-rose-700" />
                        <h3 className="font-heading text-sm font-bold text-slate-900">Alto desgaste — cuida tu energía</h3>
                        <Badge variant="outline" className="text-[10px] ml-auto">{advertenciasList.length}</Badge>
                    </div>
                    <p className="text-[11px] text-slate-500 italic leading-relaxed">Extremadamente difíciles a nivel nacional. Apunta a Competente con coherencia.</p>
                    <div className="space-y-2">
                        {advertenciasList.map((ind) => <IndicadorPill key={ind.id} ind={ind} />)}
                    </div>
                </div>
            </div>

            {/* Efecto Dominó */}
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-emerald-700" />
                    <h3 className="font-heading text-sm font-bold text-slate-900">Efecto dominó · indicadores tractores</h3>
                </div>
                <p className="text-[11px] text-slate-500 italic leading-relaxed">
                    Algunos indicadores arrastran a otros: trabajar UNO con profundidad mejora indirectamente varios. Aprovéchalos.
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                    {dominoList.map((p) => <EfectoDominoCard key={p.tractor} pieza={p} />)}
                </div>
            </div>

            {/* Clusters */}
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-slate-700" />
                    <h3 className="font-heading text-sm font-bold text-slate-900">4 macrocompetencias pedagógicas (clusters)</h3>
                </div>
                <p className="text-[11px] text-slate-500 italic leading-relaxed">
                    Los 18 indicadores no son habilidades aisladas: se organizan en grupos con perfiles de desempeño y dinámicas distintas.
                </p>
                <div className="grid gap-4 lg:grid-cols-2">
                    {clustersList.map((c) => <ClusterCard key={c.id} cluster={c} />)}
                </div>
            </div>
        </div>
    );
}
